import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Button } from '@bringg/react-components';
import { Run } from '@bringg/types';
import { RouteOptimizationResult } from '@bringg/dashboard-sdk/dist/RouteOptimization/RouteOptimization.consts';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { Notification } from '@bringg/react-components';

import {
	actions,
	assignErrorMessage,
	assignSuccessMessage,
	CloseDataType,
	getCloseData,
	UserAssignmentModalParams,
	ViewNames
} from '../consts';
import UnassignButton from '../unassign-button/unassign-button';
import AssignButton from './assign-button';
import useOptimizationFunctions from '../use-optimization-functions';
import { useHasCombinedOptimization } from 'bringg-web/features/delivery-blocks-v2/utils/use-has-combined-optimization';
import { OptimizationItemType } from 'bringg-web/features/routes-planner/models';
import useUserAssignerStore from '../use-user-assigner-store';
import { OptimizationStatus } from '../optimization-view/optimization-view';

import './user-assigner-footer.scss';

export interface UserAssignerFooterProps {
	currentView: ViewNames;
	modalParams: UserAssignmentModalParams;
	assignToRun: (run: Partial<Run>) => void;
	unassignTasks: (detachTasksFromRuns: boolean) => void;
	onCloseModal: (data?: CloseDataType) => void;
}

export type UnassignButtonTypes = 'driverAndOrders' | 'orders' | 'driverOrDriverAndOrders';

const UserAssignerFooter: React.FC<UserAssignerFooterProps> = ({
	currentView,
	modalParams,
	assignToRun,
	unassignTasks,
	onCloseModal
}: UserAssignerFooterProps) => {
	const { t } = useTranslation();

	const {
		selectedDriver,
		selectedRun,
		runParams,
		unstartedRunTasks,
		teamPlannedRoutes,
		error,
		setError,
		optimizationStatus,
		backFromOptimizationStep,
		setCurrentView,
		optimizationResult
	} = useUserAssignerStore();

	const { onApplyOptimizationResult, startOptimization, openRoutesPlanner } = useOptimizationFunctions(
		modalParams.source
	);

	const { allowedActions } = modalParams.data;

	const isCombinedOptimization = useHasCombinedOptimization();

	const isSourceAssigned = () => {
		if (modalParams.source.run) {
			return modalParams.source.run.user_id;
		}

		return modalParams.source.tasks?.some(task => task.user_id);
	};

	// Warning! tasks: undefined indicates whole route action
	const isWholeRouteAction = !modalParams.source.tasks;

	const isRouteAssignedToTasks = useMemo(() => {
		// TODO: investigate possibilities getting tasks for check from modalParams(if exist) or from tasksByRun by modalParams.source.run.id
		// const tasks = modalParams.source.run ? tasksByRun[modalParams.source.run.id] : modalParams.source.tasks;
		return modalParams.source.tasks?.some(task => task.run_id);
	}, [modalParams]);

	const getUnassignOptions = (): UnassignButtonTypes => {
		if (!isSourceAssigned()) {
			return 'orders';
		}
		return modalParams.source.isAllRunWasSelected ? 'driverOrDriverAndOrders' : 'driverAndOrders';
	};

	const assignToNewRun = () => {
		const isValid =
			runParams.scheduled_start_time &&
			runParams.scheduled_end_time &&
			(teamPlannedRoutes.length === 0 || runParams.planned_route_id);
		setError(isValid ? '' : 'RUN_USER_ASSIGNMENT.MISSING_FIELDS');

		if (isValid) {
			return assignToRun(runParams);
		}
		return undefined;
	};

	const isApplyOptimizationDisabled = () => {
		if (optimizationStatus === OptimizationStatus.FAILED) {
			return true;
		}
		return !(optimizationResult as RouteOptimizationResult).data?.some(
			task => task.type === OptimizationItemType.TASK
		);
	};

	const onApplyOptimization = async () => {
		const data = getCloseData(undefined, modalParams.data.drivers, selectedDriver.id);

		const closeModalWithData = () => {
			onCloseModal(data);
		};
		try {
			await onApplyOptimizationResult(closeModalWithData);
			Notification.success(t(assignSuccessMessage));
		} catch (e) {
			Notification.error(assignErrorMessage);
			console.error('error trying to apply optimization results', e);
		}
	};

	const sendToGoogleAnalytics = async (key: string) => {
		await getRootEnv().dashboardSdk.sdk.userAnalytics.trackEvent({
			key: `RM:${key}`
		});
	};

	return (
		<div data-test-id="user-assigner-footer">
			{currentView === 'table' && (
				<div className="table-footer" data-test-id="table-footer-buttons">
					{(!allowedActions || allowedActions.includes(actions.unassign)) && (
						<UnassignButton
							onUnassignTasks={unassignTasks}
							unassignButtonType={getUnassignOptions()}
							disabled={!isWholeRouteAction && !isRouteAssignedToTasks && !isSourceAssigned()}
						/>
					)}
					<AssignButton
						selectedRun={selectedRun}
						selectedDriver={selectedDriver}
						modalParams={modalParams}
						unstartedRunTasks={unstartedRunTasks}
						assignToRun={assignToRun}
						setError={setError}
						setView={setCurrentView}
						isCombinedOptimization={isCombinedOptimization}
						startOptimization={async () => startOptimization(setCurrentView)}
					/>
				</div>
			)}

			{currentView === 'newRoute' && (
				<>
					<span key="error" className={classNames('validation-error', { 'validation-error-visible': error })}>
						{t(error)}
					</span>
					<Button key="back" className="create-run-back" onClick={() => setCurrentView('table')}>
						{t('RUN_USER_ASSIGNMENT.BACK')}
					</Button>

					<Button key="ok" type="primary" className="create-run" onClick={assignToNewRun}>
						{t('RUN_USER_ASSIGNMENT.CREATE')}
					</Button>
				</>
			)}

			{currentView === 'optimizationView' &&
				(optimizationStatus === OptimizationStatus.NONE ? null : (
					<div className="table-footer">
						<Button
							key="back"
							className="create-run-back"
							onClick={async () => {
								await sendToGoogleAnalytics('userAssignBackFromOptimization');
								backFromOptimizationStep();
								setCurrentView('table');
							}}
							type={isApplyOptimizationDisabled() ? 'primary' : 'link'}
						>
							{t('RUN_USER_ASSIGNMENT.BACK')}
						</Button>
						<div>
							<Button
								onClick={async () => {
									await sendToGoogleAnalytics('userAssignOpenRoutesPlanner');
									openRoutesPlanner();
								}}
							>
								{t('RUN_USER_ASSIGNMENT.ROUTES_PLANNER')}
							</Button>
							<Button
								type="primary"
								disabled={isApplyOptimizationDisabled()}
								onClick={async () => {
									await sendToGoogleAnalytics('userAssignApplyOptimization');
									await onApplyOptimization();
								}}
							>
								{t('RUN_USER_ASSIGNMENT.Apply')}
							</Button>
						</div>
					</div>
				))}
		</div>
	);
};

export default observer(UserAssignerFooter);
